import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AddModal } from '../../../../component/modal';
import { colors } from '../../../../component/colors';
import { DropDownWithLabel } from '../../../../component/dropDown';
import { Input } from '../../../../component/input';
import { ImagePicker } from '../../../../component/imagePicker';
import { useFormik } from 'formik';
import { showErrorToast, showSuccessToast } from '../../../../utils/toast';
import { useFetchAgentQuery } from '../../../../store/agent/api';
import { editStoreValidation } from '../../validation';
import { useEditStoreMutation } from '../../../../store/shopping/api';
import { IStore } from '../../../../store/shopping/interface';
import { GooglePlace } from '../../../../component/googlePlace';
import useDebounce from '../../../../utils/useDebounce';
import { generateUrl } from '../../../../utils/urlGenerator';
import { SearchableDropDownWithLabel } from '../../../../component/dropDown/searchableDropDownWthLabel';
type IAddStore = {
  show?: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  storeDetails: IStore;
};
type FormikValues = {
  name: string;
  type: string;
  agent: {
    name: string;
  };
  address: {
    address: string;
    lat: number;
    lng: number;
    city: string;
  };
  state: string;
  file: File[] | null;
};
export const EditStore: FC<IAddStore> = ({ setShow, storeDetails }) => {
  const [searchAgent, setSearchAgent] = useState('');
  const debounceSearchAgent = useDebounce(searchAgent, 500);
  const { data: agents } = useFetchAgentQuery(
    generateUrl({ module: 'Shopping', search: debounceSearchAgent })
  );
  const [editStore, { isLoading: isAddingStore }] = useEditStoreMutation();
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };
  const agentsList = useMemo(() => {
    return (
      agents?.data.fetchedData.map((agents) => ({
        name: agents.name,
        imageUrl: agents.file.url
      })) ?? []
    );
  }, [agents]);

  const handleAddProperty = (values: FormikValues) => {
    const formData = new FormData();
    if (values.name !== storeDetails.name) {
      formData.append('name', values.name);
    }
    if (values.type !== storeDetails.type) {
      formData.append('type', values.type.toLowerCase());
    }
    const selectedAgent = agents?.data?.fetchedData.find((item) => {
      return item.name === values.agent.name;
    });
    if (selectedAgent && selectedAgent._id !== storeDetails.agent._id) {
      formData.append('agent', selectedAgent._id);
    }

    if (values.state !== storeDetails.state) {
      formData.append('state', values.state);
    }
    if (values.file) {
      values.file.forEach((file) => {
        formData.append('file', file);
      });
    }
    if (
      storeDetails.address.toLowerCase() !==
      values.address.address.toLowerCase()
    ) {
      formData.append('address', values.address.address);
      formData.append('coordinates[longitude]', values.address.lng.toString());
      formData.append('coordinates[latitude]', values.address.lat.toString());
    }
    editStore({ body: formData, id: storeDetails._id })
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setShow(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, touched, errors, handleChange, handleSubmit, setFieldValue } =
    useFormik({
      initialValues: {
        name: storeDetails?.name ?? '',
        type: storeDetails?.type ?? '',
        agent: { name: storeDetails?.agent?.name ?? '' },
        state: storeDetails?.state ?? '',
        address: {
          address: storeDetails.address,
          lat: storeDetails.coordinates.latitude,
          lng: storeDetails.coordinates.longitude,
          city: storeDetails.state
        },
        file: null
      },
      validationSchema: editStoreValidation,
      onSubmit: handleAddProperty
    });
  return (
    <AddModal
      title="Edit Store"
      disableBtn={isAddingStore}
      btnTxt={isAddingStore ? 'Loading...' : 'Update'}
      btnClick={handleSubmit}
      closeModal={() => setShow(false)}
      isLoading={isAddingStore}
    >
      <Container>
        <Row>
          <CustomInput
            labelText="Name"
            value={values.name}
            onChange={handleChange('name')}
            placeholder="Enter Name"
          />

          <CustomDropDown
            label="Type"
            value={values.type}
            onSelect={(item) => {
              setFieldValue('type', item);
            }}
            options={['Mall', 'Supermarket', 'Store']}
          />
        </Row>
        <Row>
          <CustomGooglePlace
            onSelect={(value) => setFieldValue('address', value)}
            placeholder="Enter address"
            defaultInputValue={storeDetails.address}
            label="Address"
            errorMessage={touched.address && errors.address ? '*required' : ''}
          />
          <SearchableCustomDropDown
            search={searchAgent}
            setSearch={setSearchAgent}
            label="SOJI agent"
            value={values.agent.name}
            onSelect={(item) => {
              setFieldValue('agent', { name: item });
            }}
            options={agentsList}
            errorMessage={touched.agent && errors.agent ? '*required' : ''}
          />
        </Row>
        <CustomImagePicker
          initialValue={storeDetails.file.map((item) => item.url)}
          onFilePicked={handleFilePicked}
        />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;
const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomImagePicker = styled(ImagePicker)`
  margin-bottom: 32px;
`;

const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomGooglePlace = styled(GooglePlace)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const SearchableCustomDropDown = styled(SearchableDropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
